import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2, Loader2, FileText, Target, Calendar, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus("no_session");
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      // Poll for payment status
      let attempts = 0;
      const maxAttempts = 10;
      
      const pollStatus = async () => {
        const response = await axios.get(`${API}/payment/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          setPaymentInfo(response.data);
          // Trigger confetti
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        } else if (response.data.status === "expired" || response.data.payment_status === "failed") {
          setStatus("failed");
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(pollStatus, 2000);
        } else {
          setStatus("pending");
        }
      };

      pollStatus();
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
        <p className="text-muted-foreground">Confirming your payment...</p>
      </div>
    );
  }

  if (status === "no_session") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">No payment session found</p>
        <Link to="/">
          <Button>Go to Home</Button>
        </Link>
      </div>
    );
  }

  if (status === "failed" || status === "error") {
    return (
      <div className="py-12">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">Payment Failed</h1>
          <p className="text-muted-foreground mb-6">
            Something went wrong with your payment. Please try again.
          </p>
          <Link to="/pricing">
            <Button className="rounded-full">Try Again</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-lg mx-auto px-4">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Welcome to FoundrScore Pro!</h1>
          <p className="text-muted-foreground">
            Your payment was successful. All Pro features are now unlocked.
          </p>
        </div>

        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-primary" />
              <h2 className="font-semibold">What's Now Available</h2>
            </div>
            <ul className="space-y-3">
              {[
                { icon: FileText, text: "Tailored Business Plan with PDF download" },
                { icon: Target, text: "Validation Canvas with pre-filled data" },
                { icon: Calendar, text: "30-Day Action Plan with task tracking" },
                { icon: CheckCircle2, text: "Day-30 Re-scoring to track progress" }
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm">{item.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <Link to="/hub" className="block">
            <Button className="w-full rounded-full" data-testid="go-to-hub-btn">
              Go to Validation Hub
            </Button>
          </Link>
          <Link to="/assessment" className="block">
            <Button variant="outline" className="w-full rounded-full">
              Take Full Assessment
            </Button>
          </Link>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-8">
          A receipt has been sent to your email. Need help?{" "}
          <a href="mailto:support@foundrscore.com" className="underline">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
